import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/cn";
import type { BadgeProps, BadgeTone } from "./Badge.types";

const badgeVariants = cva("bfrs-inline-block bfrs-min-w-0 bfrs-rounded-[var(--bfrs-radius-badge)] bfrs-font-bold bfrs-uppercase bfrs-leading-[normal] bfrs-tracking-[0.3px]", {
  variants: {
    size: {
      xs: "bfrs-px-[9px] bfrs-py-[3px] bfrs-text-[10px]",
      sm: "bfrs-px-[9px] bfrs-py-[3px] bfrs-text-[11.5px]",
      md: "bfrs-px-2.5 bfrs-py-1 bfrs-text-xs",
      lg: "bfrs-px-3 bfrs-py-1.5 bfrs-text-sm"
    },
    tone: {
      neutral: "bfrs-bg-surface-muted bfrs-text-text-secondary",
      primary: "bfrs-bg-primary bfrs-text-primary-contrast",
      brand: "bfrs-bg-primary-soft bfrs-text-primary",
      success: "bfrs-bg-success-soft bfrs-text-success-contrast",
      warning: "bfrs-bg-warning-soft bfrs-text-warning-contrast",
      danger: "bfrs-bg-danger-soft bfrs-text-danger-contrast",
      info: "bfrs-bg-info-soft bfrs-text-info-contrast"
    }
  },
  defaultVariants: {
    size: "sm",
    tone: "neutral"
  }
});

const dotVariants = {
  neutral: "bfrs-bg-text-muted",
  primary: "bfrs-bg-primary",
  brand: "bfrs-bg-primary",
  success: "bfrs-bg-success",
  warning: "bfrs-bg-warning",
  danger: "bfrs-bg-danger",
  info: "bfrs-bg-info"
} as const;

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(({ className, variant, tone, size = "sm", dot, count, maxCount = 99, children, ...props }, ref) => {
  const resolvedTone = (tone ?? (variant === "default" ? "neutral" : variant) ?? (typeof count === "number" ? "primary" : "neutral")) as BadgeTone;
  const content = typeof count === "number" ? (count > maxCount ? `${maxCount}+` : count) : children;

  return (
    <span ref={ref} className={cn(badgeVariants({ tone: resolvedTone, size }), className)} {...props}>
      {dot && <span className={cn("bfrs-mr-1 bfrs-inline-block bfrs-size-1.5 bfrs-shrink-0 bfrs-rounded-pill bfrs-align-middle", dotVariants[resolvedTone])} aria-hidden="true" />}
      {content}
    </span>
  );
});

Badge.displayName = "Badge";
